import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getApolloContext, gql } from "@apollo/client";
import { useToast } from "@src/lib/hooks/useToast";
import {
  TranscribeAudioDocument,
  TranscribeAudioMutation,
  TranscribeAudioMutationVariables,
} from "./useDictation.generated";

gql`
  mutation TranscribeAudio($audio: Upload!, $language: String) {
    transcribeAudio(audio: $audio, language: $language) {
      success
      text
      error {
        code
      }
    }
  }
`;

// A dictated prompt, not a meeting: long recordings are cut off here.
export const MAX_DICTATION_MS = 2 * 60 * 1000;

export const appendTranscript = (base: string, transcript: string): string => {
  if (!transcript) return base;
  if (!base || /\s$/.test(base)) return base + transcript;
  return `${base} ${transcript}`;
};

const isRecordingSupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia;

// The transcription API infers the container from the file extension.
const fileNameFor = (mimeType: string): string => {
  if (mimeType.includes("mp4")) return "dictation.mp4";
  if (mimeType.includes("ogg")) return "dictation.ogg";
  return "dictation.webm";
};

// Resolved after mount so server and first client render agree.
export const useIsDictationSupported = (): boolean => {
  const [isSupported, setIsSupported] = useState(false);
  useEffect(() => setIsSupported(isRecordingSupported()), []);
  return isSupported;
};

export type UseDictationOptions = {
  value: string;
  onChange: (value: string) => void;
  // How the transcript is merged into the field's text. Defaults to
  // appending with a separating space.
  join?: (base: string, transcript: string) => string;
  // Called once the transcript is in the field, when dictation was ended
  // with `stopAndSubmit` (the composer's send button while recording).
  onSubmit?: () => void;
};

export type DictationStatus = "idle" | "recording" | "transcribing";

export type Dictation = {
  isSupported: boolean;
  status: DictationStatus;
  // Starts recording, or stops and transcribes what was recorded.
  toggle: () => void;
  // Stops recording and transcribes into the field.
  stop: () => void;
  // Stops, transcribes, then calls `onSubmit` with the transcript in place.
  stopAndSubmit: () => void;
  // Discards the recording or pending transcript; nothing is written.
  abort: () => void;
  // Current input loudness, 0–1, for the recording waveform.
  getLevel: () => number;
};

export type LevelMeter = { read: () => number; close: () => void };

// Loudness from the mic stream. Best-effort: no Web Audio means a flat line.
export const createLevelMeter = (stream: MediaStream): LevelMeter | null => {
  if (typeof window.AudioContext === "undefined") return null;
  try {
    const context = new AudioContext();
    // Created after the permission prompt, so the click's activation may
    // have lapsed; a suspended context would read silence.
    if (context.state === "suspended") void context.resume().catch(() => {});
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    return {
      read: () => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += sample * sample;
        const rms = Math.sqrt(sum / samples.length);
        // Speech RMS sits around 0.02–0.2; spread that over the bar height.
        return Math.min(1, Math.sqrt(rms * 6));
      },
      close: () => void context.close(),
    };
  } catch {
    return null;
  }
};

export const useDictation = ({
  value,
  onChange,
  join = appendTranscript,
  onSubmit,
}: UseDictationOptions): Dictation => {
  const { addErrorToast } = useToast();
  // The client from context rather than useMutation, which throws without
  // a provider: every AI input mounts this hook, even where it goes unused.
  const client = useContext(getApolloContext()).client;
  const isSupported = useIsDictationSupported();
  const [status, setStatus] = useState<DictationStatus>("idle");
  const recorderRef = useRef<MediaRecorder | null>(null);
  // Bumped to invalidate an in-flight recording or transcription.
  const sessionRef = useRef(0);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const joinRef = useRef(join);
  joinRef.current = join;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const meterRef = useRef<LevelMeter | null>(null);
  // The session ended with `stopAndSubmit`, and whether its submit is due on
  // the next render.
  const submitSessionRef = useRef(0);
  const [submitDue, setSubmitDue] = useState(false);

  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const releaseRecorder = useCallback(() => {
    clearTimeout(autoStopTimerRef.current);
    const recorder = recorderRef.current;
    recorderRef.current = null;
    recorder?.stream.getTracks().forEach((track) => track.stop());
    meterRef.current?.close();
    meterRef.current = null;
    return recorder;
  }, []);

  const abort = useCallback(() => {
    sessionRef.current += 1;
    submitSessionRef.current = 0;
    setSubmitDue(false);
    const recorder = releaseRecorder();
    if (recorder?.state === "recording") recorder.stop();
    setStatus("idle");
  }, [releaseRecorder]);

  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      releaseRecorder();
    };
  }, [releaseRecorder]);

  // The field was cleared (usually a submit) mid-dictation: drop the
  // recording rather than refill the emptied field.
  const previousValueRef = useRef(value);
  useEffect(() => {
    const cleared = value === "" && previousValueRef.current !== "";
    previousValueRef.current = value;
    if (cleared && status !== "idle") abort();
  }, [value, status, abort]);

  const transcribe = useCallback(
    async (audio: Blob, session: number) => {
      const reportFailure = (message: string) => {
        if (sessionRef.current === session) addErrorToast({ message });
      };
      try {
        if (!client) throw new Error("No Apollo client");
        const { data } = await client.mutate<
          TranscribeAudioMutation,
          TranscribeAudioMutationVariables
        >({
          mutation: TranscribeAudioDocument,
          variables: {
            audio: new File([audio], fileNameFor(audio.type), {
              type: audio.type,
            }),
            language: navigator.language,
          },
        });
        const result = data?.transcribeAudio;
        if (sessionRef.current !== session) return;
        if (result?.success) {
          const text = result.text?.trim() ?? "";
          if (text) {
            onChangeRef.current(joinRef.current(valueRef.current, text));
          }
          // Even with nothing heard, send what was already typed.
          if (submitSessionRef.current === session) setSubmitDue(true);
        } else if (result?.error?.code === "RATE_LIMITED") {
          reportFailure("You've reached the dictation limit. Try again later.");
        } else {
          reportFailure("Couldn't transcribe that recording. Try again.");
        }
      } catch {
        reportFailure("Couldn't transcribe that recording. Try again.");
      } finally {
        if (sessionRef.current === session) setStatus("idle");
      }
    },
    [addErrorToast, client],
  );

  const start = useCallback(async () => {
    const session = ++sessionRef.current;
    submitSessionRef.current = 0;
    setSubmitDue(false);
    setStatus("recording");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (sessionRef.current !== session) return;
      setStatus("idle");
      addErrorToast({
        message: "Allow microphone access in your browser to use dictation.",
      });
      return;
    }
    if (sessionRef.current !== session) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
      meterRef.current = createLevelMeter(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        if (sessionRef.current !== session) return;
        releaseRecorder();
        // Some browsers leave mimeType blank; WebM is what they record.
        const audio = new Blob(chunks, {
          type: recorder.mimeType || "audio/webm",
        });
        if (audio.size === 0) {
          setStatus("idle");
          // Nothing recorded: stop-and-send still sends the typed draft.
          if (submitSessionRef.current === session) setSubmitDue(true);
          return;
        }
        setStatus("transcribing");
        void transcribe(audio, session);
      };
      recorderRef.current = recorder;
      recorder.start();
    } catch {
      recorderRef.current = null;
      stream.getTracks().forEach((track) => track.stop());
      meterRef.current?.close();
      meterRef.current = null;
      setStatus("idle");
      addErrorToast({ message: "Couldn't start recording. Try again." });
      return;
    }
    autoStopTimerRef.current = setTimeout(() => {
      if (recorderRef.current === recorder && recorder.state === "recording") {
        recorder.stop();
        setStatus("transcribing");
      }
    }, MAX_DICTATION_MS);
  }, [addErrorToast, releaseRecorder, transcribe]);

  // Submit on the render after the transcript was handed to the parent, so
  // its submit handler reads the new value rather than a stale one.
  useEffect(() => {
    if (!submitDue) return;
    setSubmitDue(false);
    submitSessionRef.current = 0;
    onSubmitRef.current?.();
  }, [submitDue]);

  const stop = useCallback(() => {
    if (status !== "recording") return;
    const recorder = recorderRef.current;
    if (!recorder) {
      // Still waiting on microphone permission.
      abort();
    } else if (recorder.state === "recording") {
      recorder.stop();
      // onstop fires asynchronously; leave "recording" now so a second
      // click cannot abort the clip that is about to be transcribed.
      setStatus("transcribing");
    }
  }, [status, abort]);

  const stopAndSubmit = useCallback(() => {
    if (status !== "recording") return;
    if (!recorderRef.current) {
      // Nothing recorded yet (permission prompt open): send the typed draft.
      abort();
      setSubmitDue(true);
      return;
    }
    submitSessionRef.current = sessionRef.current;
    stop();
  }, [status, stop, abort]);

  const toggle = useCallback(() => {
    if (status === "idle") {
      void start();
    } else {
      stop();
    }
  }, [status, start, stop]);

  const getLevel = useCallback(() => meterRef.current?.read() ?? 0, []);

  return {
    isSupported,
    status,
    toggle,
    stop,
    stopAndSubmit,
    abort,
    getLevel,
  };
};
